/**
 * Internal role and permission model.
 *
 * Phase 1 decision (see docs/architecture/adr/0005-role-model.md):
 * roles are resolved SERVER-SIDE from an application-internal assignment
 * (Cosmos `core` container + bootstrap configuration). Entra ID app roles
 * remain the documented upgrade path; the mapping helper `highestRole()`
 * already accepts a claim-provided role list so switching over is a small,
 * isolated change.
 *
 * The frontend may use this model to hide UI, but it is NEVER the
 * authorisation boundary - every protected endpoint re-evaluates it.
 */
export const ROLES = ['Administrator', 'Editor', 'Reader'];
/**
 * Fine grained permissions. Phase 1 only enforces a handful of them, but the
 * full list documents the intended target state and keeps role tables honest.
 */
export const PERMISSIONS = [
    'platform:status:read',
    'settings:read',
    'settings:write',
    'users:manage',
    'integrations:manage',
    'customers:read',
    'customers:write',
    'documents:read',
    'documents:write',
    'documents:publish',
    'assets:read',
    'assets:write',
    'share:create',
    'share:revoke',
    'export:pdf',
    'audit:read',
    'answers:read',
    'answers:write',
    'objects:read',
    'objects:write',
    'measurements:read',
    'measurements:write',
];
const READER_PERMISSIONS = [
    'settings:read',
    'customers:read',
    'documents:read',
    'assets:read',
    // Answering open questions is deliberately available from Reader upward: the
    // people who answer are subject matter experts, not platform editors, and the
    // payload contains no customer data.
    'answers:read',
    'answers:write',
    'objects:read',
    'measurements:read',
];
const EDITOR_PERMISSIONS = [
    ...READER_PERMISSIONS,
    'customers:write',
    'documents:write',
    'documents:publish',
    'assets:write',
    'export:pdf',
    'objects:write',
    'measurements:write',
    // Share link creation is intentionally NOT granted to editors by default.
    // It becomes available per-tenant via the `editorsMayCreateShareLinks` setting.
];
const ADMINISTRATOR_PERMISSIONS = [...PERMISSIONS];
export const ROLE_PERMISSIONS = Object.freeze({
    Administrator: ADMINISTRATOR_PERMISSIONS,
    Editor: EDITOR_PERMISSIONS,
    Reader: READER_PERMISSIONS,
});
/** Role ranking, used to pick the strongest role when several are assigned. */
const ROLE_RANK = Object.freeze({
    Administrator: 30,
    Editor: 20,
    Reader: 10,
});
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
export function permissionsForRole(role) {
    return ROLE_PERMISSIONS[role];
}
export function hasPermission(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
/**
 * Picks the strongest known role from an arbitrary claim/assignment list and
 * ignores unknown values. Returns `undefined` when nothing maps - callers must
 * then fall back to the configured default role (fail closed, never fail open).
 */
export function highestRole(candidates) {
    if (!candidates || candidates.length === 0) {
        return undefined;
    }
    let best;
    for (const candidate of candidates) {
        if (!isRole(candidate)) {
            continue;
        }
        if (best === undefined || ROLE_RANK[candidate] > ROLE_RANK[best]) {
            best = candidate;
        }
    }
    return best;
}
/** Where a resolved role came from - surfaced in `/api/me` and audit events. */
export const ROLE_SOURCES = ['assignment', 'bootstrapAdmin', 'default'];
//# sourceMappingURL=roles.js.map